import type { FC } from "react";

import { hasValue } from "@arti/utilities";

import type { TaskSnapshot } from "../types";

/**
 * Helper to safely extract validation errors from task result
 */
export function getValidationErrors(
  result: TaskSnapshot["result"],
): string[] | null {
  if (!result?.validation_errors || !Array.isArray(result.validation_errors)) {
    return null;
  }
  return result.validation_errors.filter(
    (e: unknown): e is string => typeof e === "string",
  );
}

/**
 * Helper to safely extract expected schema name from task result
 */
function getExpectedSchemaName(result: TaskSnapshot["result"]): string | null {
  if (!result?.expected_schema_name) {
    return null;
  }
  return typeof result.expected_schema_name === "string"
    ? result.expected_schema_name
    : null;
}

const SchemaFileIcon: FC<{ className?: string }> = ({
  className = "h-3 w-3",
}) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
      clipRule="evenodd"
    />
  </svg>
);

const ValidationWarningIcon: FC = () => (
  <SchemaFileIcon className="h-5 w-5 text-rose-700 dark:text-rose-300" />
);

const ValidationErrorIcon: FC = () => (
  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
      clipRule="evenodd"
    />
  </svg>
);

const DisclosureChevronIcon: FC = () => (
  <svg
    className="h-3.5 w-3.5 transition-transform group-open:rotate-90"
    fill="currentColor"
    viewBox="0 0 20 20"
  >
    <path
      fillRule="evenodd"
      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const ValidationJsonDetails: FC<{ children: string; title: string }> = ({
  children,
  title,
}) => (
  <details className="group rounded-none border border-rose-200 bg-white/40 dark:border-rose-800/40 dark:bg-rose-950/20">
    <summary className="cursor-pointer px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-rose-800 transition hover:text-rose-900 dark:text-rose-200/90 dark:hover:text-rose-100">
      <span className="inline-flex items-center gap-1.5">
        <DisclosureChevronIcon />
        {title}
      </span>
    </summary>
    <div className="px-3 pb-3">
      <pre className="mt-2 max-h-64 overflow-auto rounded-none bg-gradient-to-br from-rose-900 to-rose-950 p-3 text-xs leading-relaxed text-rose-50 shadow-inner dark:from-slate-950 dark:to-rose-950/50 dark:text-rose-100">
        {children}
      </pre>
    </div>
  </details>
);

const SchemaBadge: FC<{ name: string }> = ({ name }) => (
  <span className="inline-flex items-center gap-1.5 border border-rose-300 bg-rose-100 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-rose-800 dark:border-rose-500/40 dark:bg-rose-950/30 dark:text-rose-200">
    <SchemaFileIcon />
    {name}
  </span>
);

/**
 * Validation Failure Display Component
 * Shows schema validation errors with details about expected schema and actual result
 */
export const ValidationFailureSection: FC<{ task: TaskSnapshot }> = ({
  task,
}) => {
  const validationErrors = getValidationErrors(task.result);
  const schemaName = getExpectedSchemaName(task.result);
  const expectedSchema = task.result?.expected_schema;
  const actualResult = task.result?.actual_result;

  if (!validationErrors || validationErrors.length === 0) {
    return null;
  }

  return (
    <div className="rounded-none border border-rose-300 bg-rose-50/90 p-3 shadow-[4px_4px_0_rgba(136,19,55,0.08)] dark:border-rose-500/30 dark:bg-rose-950/20 dark:shadow-[4px_4px_0_rgba(136,19,55,0.24)]">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex-shrink-0 border border-rose-300 bg-rose-100 p-2 dark:border-rose-500/40 dark:bg-rose-950/40">
          <ValidationWarningIcon />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="font-mono text-sm uppercase tracking-[0.18em] text-rose-900 dark:text-rose-100">
            Schema Validation Failed
          </h4>
          <p className="mt-0.5 text-xs text-rose-700 dark:text-rose-300/90">
            Response did not match the expected schema
          </p>
        </div>
        {schemaName && (
          <div className="hidden flex-shrink-0 sm:block">
            <SchemaBadge name={schemaName} />
          </div>
        )}
      </div>

      {schemaName && (
        <div className="mb-4 sm:hidden">
          <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-rose-700 dark:text-rose-300/80">
            Schema
          </p>
          <SchemaBadge name={schemaName} />
        </div>
      )}

      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-rose-800 dark:text-rose-200/90">
          Validation Errors ({validationErrors.length})
        </p>
        <ul className="space-y-2">
          {validationErrors.map((error, idx) => (
            <li
              key={idx}
              className="flex items-start gap-2.5 rounded-none border border-rose-200 bg-white/60 px-3 py-2.5 dark:border-rose-800/40 dark:bg-rose-950/30"
            >
              <span className="mt-0.5 flex-shrink-0 text-rose-600 dark:text-rose-400">
                <ValidationErrorIcon />
              </span>
              <span className="flex-1 break-words font-mono text-sm leading-relaxed text-rose-900 dark:text-rose-100">
                {error}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {(hasValue(expectedSchema) || actualResult !== undefined) && (
        <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
          {expectedSchema && (
            <ValidationJsonDetails title="Expected Schema">
              {JSON.stringify(expectedSchema, null, 2)}
            </ValidationJsonDetails>
          )}

          {actualResult !== undefined && (
            <ValidationJsonDetails title="Actual Result Received">
              {JSON.stringify(actualResult, null, 2)}
            </ValidationJsonDetails>
          )}
        </div>
      )}
    </div>
  );
};
